// TypeScript mirror of the `waggle-proto` message catalogue and LOD record types
// (docs/dev/wire-protocol.md, PROTOCOL §3). These are pure data shapes; the byte
// codec lives in `codec.ts`. Field names are camelCased mirrors of the Rust
// snake_case fields. `u64` fields (seq, base_seq, highest_contiguous_seq) are
// `bigint` so the full range round-trips losslessly; every other integer is a
// `number`. Enums carry their exact wire discriminant value.

/** The wire protocol version this client speaks (`waggle-proto::version`). Bumped 2 → 3 by
 * `colony-field-canvas` (appending `Welcome.your_hive`), then 3 → 4 by `replay-viewer`
 * (adding the client→server `ReplayControl` message, design D9), then 4 → 5 by
 * `beekeeper-wire-and-hand-edit` (adding `RejectReason.compileFailed { diagnostics }`),
 * 5 → 6 / 6 → 7 by the close-tier dance and cell records, 7 → 8 by `wire-entity-detail`
 * (the close-tier `BeeRecord` gained `colonyId`/`typeId`/`health`/`nectar` and
 * `PatchRecord.hasNectar` widened to the quantized `pool`), 8 → 9 by `wire-gathering-bit`
 * (the packed facing/action byte's spare bit 6 became `BeeRecord.gathering`, narrowing the
 * reserved mask to bit 7 — a wire *semantic* change at an unchanged record size, which the repo
 * bumps on just the same), and 9 → 10 by `combat-strikes-and-damage-flash` (the `CellRecord`
 * `state` byte's last reserved bit — bits 3-7 — became the five-bit `hpBucket`, spending the
 * one bit `wire-gathering-bit` had left; the record stays 9 wire bytes), and 10 → 11 by the
 * live/rounds consolidation (whole-world detail collections replace the live viewport cap),
 * 11 → 12 by `deathmatch-world` (the appended `ManifestSummary.playMarginU`), and 12 → 13 by
 * WAG-107 (the appended `ManifestSummary.speedMultiplier` — the stream's wall-clock pacing).
 * Older clients are rejected with the current `minVersion`. */
export const PROTOCOL_VERSION = 13;

// --- small tag enums (wire discriminant = numeric value) ---

export enum Tier {
  Wide = 0,
  Close = 1,
}

export enum ActionClass {
  Forage = 0,
  Build = 1,
  Fight = 2,
  Idle = 3,
}

export enum Season {
  Summer = 0,
  Autumn = 1,
  Winter = 2,
}

export enum BuffName {
  Health = 0,
  FoodConsumption = 1,
  Capacity = 2,
  Strength = 3,
  Speed = 4,
  Building = 5,
  Range = 6,
}

// --- LOD record types (lod.rs) ---

export interface BlobRecord {
  blobId: number;
  centroid: [number, number];
  count: number;
  dominantAction: ActionClass;
}

export interface QueenRecord {
  id: number;
  colonyId: number;
  position: [number, number];
}

/**
 * A patch summary record, carried at both tiers. `pool` is the patch's remaining nectar as a
 * quantized fraction of its own maximum (`Balance::forage.patch_richness_max`): 0 = exhausted,
 * 255 = full. It replaces — and strictly subsumes — the former `hasNectar: boolean`, because the
 * quantization floors any strictly-positive pool at 1: `pool > 0` is exactly the old `true`.
 */
export interface PatchRecord {
  id: number;
  position: [number, number];
  /** Remaining nectar as a quantized fraction of the patch's maximum pool (0 = empty, 255 = full). */
  pool: number;
}

export interface RuinRecord {
  id: number;
  position: [number, number];
}

/**
 * A close-tier per-bee record. On the wire, `facing` (0..11) and `action` are
 * packed into one byte (low nibble facing, bits 4-5 the action class); the codec
 * unpacks them into these two fields.
 *
 * `colonyId`, `typeId`, `health` and `nectar` are the `wire-entity-detail` fields: enough to
 * colour a bee by its colony and type and to draw its vitals without a second round trip.
 * `health`/`nectar` are quantized fractions of **that bee's own** maxima (its buffed max health
 * and its buffed carrying capacity), so a ×2-buffed bee and a base bee both read 255 at full —
 * divide by 255 to get the fraction, never by a balance constant.
 */
export interface BeeRecord {
  id: number;
  position: [number, number];
  facing: number;
  action: ActionClass;
  /**
   * Whether this bee **actually drew nectar in its current decision window**
   * (wire-gathering-bit): the sim's own `GatheredNectar` flag, the same state the
   * `not_gathering` B++ sense reads, carried in bit 6 of the packed facing/action byte.
   *
   * Never re-derive this from {@link action}: `ActionClass.Forage` collapses `CollectNectar`,
   * `DropNectar`, `ReturnToHive`, `MoveTo`, `Patrol`, `Wander`, `Follow` and `Dance` into one
   * value, and even the underlying `collect_nectar` is true of a bee still walking toward a
   * patch, one capped out of spare capacity, and one that lost a claimant contest. This flag is
   * the only authoritative gather signal on the wire (it closed `docs/dev/deferred.md` D-147).
   */
  gathering: boolean;
  /** The owning colony's logical id. */
  colonyId: number;
  /** The colony-private bee type index, or `null` for the typeless queen. */
  typeId: number | null;
  /** Health as a quantized fraction of this bee's max health: 0 = dead, 255 = full. */
  health: number;
  /** Carried nectar as a quantized fraction of this bee's capacity: 0 = empty, 255 = full. */
  nectar: number;
}

export interface ClosePayload {
  records: BeeRecord[];
  truncated: boolean;
  totalCount: number;
}

/**
 * A close-tier dance record (dance-visuals): the observation-only projection of an active
 * `dance(pt, tag)` action. `dancerId` matches a {@link BeeRecord} id — the arrow origin is
 * resolved from the dancer's own record (this carries no position). `target` is the dance
 * target quantized to the u16 field grid; `tagHash` is a cosmetic grouping hue, never the
 * raw colony-private tag.
 */
export interface DanceRecord {
  dancerId: number;
  target: [number, number];
  tagHash: number;
}

/**
 * A close-tier per-cell hive record (hive-egg-cells): the observation-only projection of one
 * `waggle_sim::Cell`. `id` is the cell's own logical id (a distinct namespace from bees).
 * `position` is quantized to the u16 field grid like a bee position. The wire `state` byte's
 * three orthogonal flags are decoded into booleans here: `occupied` (a brood occupant — the
 * "has an egg" signal), `damaged` (`hp < cell_hp`), and `ruin` (the cell's colony is
 * eliminated). A cell can be both `damaged` and `occupied`.
 *
 * `hpBucket` is the `combat-strikes-and-damage-flash` field: the wire `state` byte's bits 3-7,
 * a **five-bit quantization of this cell's own `hp / max_hp`** (0..31; 0 is unreachable for a
 * live cell — any strictly positive fraction rounds up to at least 1). Exposed as the **raw
 * bucket**, never converted to hp or a percentage, the same discipline {@link BeeRecord.health}
 * follows in exposing the raw 0..255 wire fraction: no client-side consumer divides by a
 * balance constant (`cell_hp`) the client does not have. `hpBucket` MUST NOT be read as a
 * substitute for `damaged` — quantization rounds ties even, so a nearly-full damaged cell still
 * lands in the top bucket (31); `damaged` remains the exact "below full hp" predicate, `hpBucket`
 * is only magnitude. With this field the packed `state` byte now uses all eight bits — decoding
 * it no longer masks away bits 3-7 (there is no reserved bit left, `codec.ts`'s
 * `decodeCellRecord`).
 */
export interface CellRecord {
  id: number;
  position: [number, number];
  occupied: boolean;
  damaged: boolean;
  ruin: boolean;
  /** Raw 0..31 hp bucket (state byte bits 3-7). Unconverted — divide by 31 yourself if a
   * fraction is ever needed; no consumer should. */
  hpBucket: number;
}

/** The whole-world close-tier cell set. `truncated` is retained for wire compatibility and
 * defensive decoding; the rounds-style projector does not intentionally drop cells. */
export interface CellPayload {
  records: CellRecord[];
  truncated: boolean;
}

// --- keyframe / delta (keyframe_delta.rs) ---

export interface WideKeyframe {
  seq: bigint;
  tick: number;
  blobs: BlobRecord[];
  queens: QueenRecord[];
  patches: PatchRecord[];
  ruins: RuinRecord[];
}

export interface CloseKeyframe {
  seq: bigint;
  tick: number;
  bees: ClosePayload;
  patches: PatchRecord[];
  /** Retained wire slot for dance records; rounds-style projections leave it empty. */
  dances: DanceRecord[];
  /** The whole-world hive-cell set, bounded only by the defensive decoder limit. */
  cells: CellPayload;
}

export type Keyframe =
  | ({ tier: Tier.Wide } & WideKeyframe)
  | ({ tier: Tier.Close } & CloseKeyframe);

export type WideUpsert =
  | { kind: 'blob'; value: BlobRecord }
  | { kind: 'queen'; value: QueenRecord }
  | { kind: 'patch'; value: PatchRecord }
  | { kind: 'ruin'; value: RuinRecord };

export type CloseUpsert =
  | { kind: 'bee'; value: BeeRecord }
  | { kind: 'patch'; value: PatchRecord }
  | { kind: 'dance'; value: DanceRecord }
  | { kind: 'cell'; value: CellRecord };

export type WideEntityKey =
  | { kind: 'blob'; id: number }
  | { kind: 'queen'; id: number }
  | { kind: 'patch'; id: number }
  | { kind: 'ruin'; id: number };

export type CloseEntityKey =
  | { kind: 'bee'; id: number }
  | { kind: 'patch'; id: number }
  | { kind: 'dance'; id: number }
  | { kind: 'cell'; id: number };

export interface WideDelta {
  seq: bigint;
  baseSeq: bigint;
  tick: number;
  upserts: WideUpsert[];
  deletes: WideEntityKey[];
}

export interface CloseDelta {
  seq: bigint;
  baseSeq: bigint;
  tick: number;
  upserts: CloseUpsert[];
  deletes: CloseEntityKey[];
}

export type Delta =
  | ({ tier: Tier.Wide } & WideDelta)
  | ({ tier: Tier.Close } & CloseDelta);

// --- server -> client messages (messages.rs / version.rs) ---

export interface ManifestSummary {
  tickRateHz: number;
  fieldSize: number;
  colonyIds: number[];
  /** The match-level dance audibility radius (dance-visuals), from the active balance
   * preset — the client's watcher-derivation radius. Appended after `colonyIds`. */
  danceRadius: number;
  /** The **play margin** (`world.play_margin_u`): the apron beyond the field edge bees may
   * move into, in world units — 0 on every world that does not configure one. The boundary
   * bees actually stop at is `fieldSize / 2 + playMarginU`, which is what the perimeter is
   * drawn at (`deathmatch-world` D-6). Rides beside `fieldSize` rather than redefining it:
   * `fieldSize` still drives LOD viewport derivation and blob decoding. Appended after
   * `danceRadius`. */
  playMarginU: number;
  /** The **wall-clock pacing of this stream** (WAG-107): ticks advance at
   * `tickRateHz * speedMultiplier` per wall second. `1.0` everywhere except the pvp
   * prototype's accelerated match, and `1.0` for a replay (whose pacing is the viewer's own
   * `ReplayControl`).
   *
   * Read it **only** to state wall time — the Match screen's countdown is its one consumer.
   * `tickRateHz` remains ticks per *sim* second and is what every animation, age and season
   * conversion divides by; multiplying those by this would double-count the acceleration.
   * Appended after `playMarginU`. */
  speedMultiplier: number;
}

export interface Welcome {
  tick: number;
  protocolVersion: number;
  manifest: ManifestSummary;
  yourColony: number | null;
  /** The own colony's static hive world point `[hx, hy]` — the colony play view's camera
   * anchor (`colony-field-canvas`) — or `null` for a spectator. */
  yourHive: [number, number] | null;
}

export interface ScoreboardRow {
  colonyId: number;
  livingCount: number;
  pool: number;
}

export interface MatchStateObs {
  season: Season;
  viabilityRadius: number;
  clockRemainingTicks: number;
  eliminations: number[];
  scoreboard: ScoreboardRow[];
}

export interface MatchState {
  tick: number;
  state: MatchStateObs;
}

export interface TypeMeta {
  typeId: number;
  name: string;
  buffName: string;
  activeVersion: number;
  canonicalText: string;
}

export interface VersionHead {
  typeId: number;
  headVersion: number;
}

/** One diagnostic against the rounds working document (CONTRACT.md §6's colony DTO addition) —
 * the document's own line/column, the same coordinates the Code tab's degraded view marks. */
export interface DocumentDiagnostic {
  message: string;
  line: number;
  column: number;
}

export interface ColonyMeta {
  tick: number;
  types: TypeMeta[];
  birthType: number;
  versionHeads: VersionHead[];
  /**
   * The rounds sandbox's whole working document (`bpp-colony-document`), verbatim bytes.
   * `undefined` in live mode and until a rounds `GET /colony` has been read — its mere presence is
   * what `Colony.tsx`'s Code tab reads as "render the document's own bytes" rather than the
   * per-type `canonicalText` (client-colony "Read-only code view, editable via HandEdit").
   */
  document?: string;
  /** Whether `document` currently parses/compiles. `undefined` in live mode. */
  documentValid?: boolean;
  /** Diagnostics against `document` when `documentValid === false`. */
  documentDiagnostics?: DocumentDiagnostic[];
  /** Whether a Beekeeper turn is in flight for this sandbox — gates the Code tab's hand-edit
   * affordance (client-colony "Hand-editing is unavailable while a turn runs"). `undefined` in
   * live mode, where no such concept exists and the gate is always open. */
  turnInFlight?: boolean;
  /**
   * The rounds working document's own version ordinal (`rounds-sandbox-state`'s "document
   * version history") — what a rounds `HandEdit`'s `baseVersion` reads (client-colony: "the
   * document is the edited unit in this mode", never `TypeMeta.activeVersion`). `undefined` in
   * live mode and until a value has actually been observed (a `GET /colony` that does not carry
   * one yet, or no rounds hand-edit save has landed this session) — `rounds-prompt.ts` treats
   * that the same as "no known base" rather than inventing an ordinal.
   */
  documentVersion?: number;
}

export type PromptStatusKind =
  | { kind: 'idle' }
  | { kind: 'generating' }
  | { kind: 'repairing'; attempt: number }
  | { kind: 'accepted'; version: number }
  | { kind: 'failed'; diagnostics: string[] }
  | { kind: 'stale' };

export interface PromptStatus {
  tick: number;
  typeId: number;
  status: PromptStatusKind;
}

export interface Placement {
  colonyId: number;
  rank: number;
  livingCount: number;
  pool: number;
}

export interface MatchResult {
  tick: number;
  placements: Placement[];
  timelineRef: string;
}

export interface StateBucket {
  stateName: string;
  count: number;
}

export interface TypeHistogram {
  typeId: number;
  buckets: StateBucket[];
}

export interface StateHistogram {
  tick: number;
  perType: TypeHistogram[];
}

export type RejectReason =
  | { kind: 'upgrade'; minVersion: number }
  | { kind: 'unauthorized' }
  | { kind: 'rateLimited' }
  | { kind: 'stale' }
  | { kind: 'invalid' }
  | { kind: 'compileFailed'; diagnostics: string[] }
  /**
   * A rounds hand edit (per-type or whole-document) was refused because a Beekeeper turn holds
   * the sandbox's in-flight slot (`rounds-sandbox-state`'s turn exclusivity; `409
   * turn_in_progress` in `rounds-http.ts`). HTTP/rounds-only — never decoded off the live binary
   * wire (`codec.ts`'s `decodeRejectReason` has no discriminant for it), so this carries no
   * `PROTOCOL_VERSION` bump.
   */
  | { kind: 'turnInProgress' };

export interface Rejected {
  tick: number;
  reason: RejectReason;
}

export type ServerMessage =
  | { type: 'welcome'; value: Welcome }
  | { type: 'keyframe'; value: Keyframe }
  | { type: 'delta'; value: Delta }
  | { type: 'matchState'; value: MatchState }
  | { type: 'colonyMeta'; value: ColonyMeta }
  | { type: 'promptStatus'; value: PromptStatus }
  | { type: 'result'; value: MatchResult }
  | { type: 'rejected'; value: Rejected }
  | { type: 'stateHistogram'; value: StateHistogram };

// --- client -> server messages ---

export interface Hello {
  protocolVersion: number;
  sessionToken: string;
  matchId: string;
}

export interface ViewportRect {
  min: [number, number];
  max: [number, number];
}

export interface Subscribe {
  viewport: ViewportRect;
  tier: Tier;
}

export interface Prompt {
  typeName: string;
  promptText: string;
}

export interface HandEdit {
  typeName: string;
  programSource: string;
  baseVersion: number;
}

export interface Rollback {
  typeName: string;
  targetVersion: number;
}

export interface SetBirthType {
  typeName: string;
}

export type InitialProgram =
  | { kind: 'source'; source: string }
  | { kind: 'preset'; preset: string };

export interface CreateType {
  name: string;
  buff: BuffName;
  initialProgram: InitialProgram;
}

export interface Ack {
  highestContiguousSeq: bigint;
}

/** The replay transport control (PROTOCOL §3 `ReplayControl`, PROTOCOL_VERSION 4): a
 * **spectator-only** playback verb carried by a replay session — never a colony
 * mutation. Exactly one verb per message; the server clamps a `setSpeed` multiplier to
 * `replay_speed_max`. There is no seek verb (read-only position strip; the only
 * reposition is `restart`, re-run from tick 0 — D-123). Mirrors the `waggle-proto`
 * `ReplayControl` enum (`Pause` 0, `Resume` 1, `SetSpeed` 2, `Restart` 3). */
export type ReplayControlMessage =
  | { kind: 'pause' }
  | { kind: 'resume' }
  | { kind: 'setSpeed'; multiplier: number }
  | { kind: 'restart' };

export type ClientMessage =
  | { type: 'hello'; value: Hello }
  | { type: 'subscribe'; value: Subscribe }
  | { type: 'prompt'; value: Prompt }
  | { type: 'handEdit'; value: HandEdit }
  | { type: 'rollback'; value: Rollback }
  | { type: 'setBirthType'; value: SetBirthType }
  | { type: 'createType'; value: CreateType }
  | { type: 'ack'; value: Ack }
  | { type: 'replayControl'; value: ReplayControlMessage };
